import { useEffect, useState } from "react"

import { DateRange } from "react-day-picker"

import { CalendarIcon, X } from "lucide-react"

import { Button } from "@ui/button"
import { Calendar } from "@ui/calendar"
import { Label } from "@ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover"

interface DateSectionProps {
    section: string
    from: string | undefined
    to: string | undefined
    onClear: number
    onFromValueChange: (selection: any) => void
    onToValueChange: (selection: any) => void
}
export function DateSection({ section, from, to, onClear, onFromValueChange, onToValueChange }: Readonly<DateSectionProps>) {
    const [isFromRange, setIsFromRange] = useState<boolean>(true)

    const [range, setRange] = useState<DateRange | undefined>({
        from: from ? new Date(from) : undefined,
        to: to ? new Date(to) : undefined
    })

    const handleSetRange = (newRange: DateRange | undefined) => {
        let rangeState = isFromRange
        if (!newRange) {
            rangeState = !rangeState
            setIsFromRange(rangeState)
        }

        if (!rangeState && newRange?.from && !newRange?.to) {
            newRange = {
                from: undefined,
                to: newRange?.from
            }
        }
        setRange(newRange)
    }

    useEffect(() => {
        onFromValueChange(range?.from)
        onToValueChange(range?.to)
    }, [range])

    useEffect(() => {
        setRange(undefined)
    }, [onClear])

    return (
        <div className="flex flex-col gap-1">
            <Label className="font-light">{section}</Label>
            <div className="flex w-full items-center gap-2">
                <Popover>
                    <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full bg-white">
                            <CalendarIcon />
                            {
                                dateLabelFormatter(range?.from, range?.to)
                            }
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full overflow-hidden p-0">
                        <Calendar
                            className="w-full"
                            mode="range"
                            defaultMonth={range?.from}
                            selected={range}
                            onSelect={handleSetRange}
                            fixedWeeks
                            showOutsideDays
                            disabled={{
                                after: new Date(),
                            }}
                        />
                    </PopoverContent>
                </Popover>
                {range && <Button variant="destructive" onClick={() => setRange(undefined)}><X /></Button>}
            </div>
        </div>
    )
}

function dateLabelFormatter(from: Date | undefined, to: Date | undefined) {
    if (from && to) {
        return <span className='w-full'>{from.toLocaleDateString()} - {to.toLocaleDateString()}</span>
    }
    if (from) {
        return <span className='w-full text-start'>From: {from.toLocaleDateString()}</span>
    }
    if (to) {
        return <span className='w-full text-start'>To: {to.toLocaleDateString()}</span>
    }
    return <span className='w-full text-start font-light'>Select a date range</span>
}